import type { ChangeType } from '../../types/changes';

export const TYPE_LABELS: Record<string, string> = {
  normal: 'Mudança Normal',
  normal_correcao: 'Mudança Normal (Correção)',
  emergencial: 'Mudança Emergencial',
  emergencial_retroativa: 'Mudança Emergencial Retroativa',
  padrao: 'Mudança Padrão',
};

export const STATUS_LABELS: Record<string, string> = {
  avaliar: 'Avaliar',
  agendado: 'Agendado',
  autorizar: 'Autorizar',
  implementar: 'Implementar',
  transporte: 'Transporte',
  revisar: 'Revisar',
  encerrar: 'Encerrar',
  cancelada: 'Cancelada',
};

/** Trilho principal exibido no detalhe (sem Implementar nem Cancelada). */
export const CHANGE_STATUS_PIPELINE: string[] = [
  'avaliar',
  'agendado',
  'autorizar',
  'transporte',
  'implementar',
  'revisar',
  'encerrar',
];

export const SUBTASK_STATUS_LABELS: Record<string, string> = {
  em_andamento: 'Em andamento',
  em_atendimento: 'Em atendimento',
  concluida: 'Concluída',
};

export function subtaskListStatus(
  subtask: { status?: string | null; invalidated_at?: string | null },
): string {
  if (subtask.invalidated_at) return 'cancelada';
  return subtask.status || 'em_andamento';
}

export const STATUS_FILTER_OPTIONS = Object.entries(STATUS_LABELS)
  .filter(([value]) => value !== 'implementar')
  .map(([value, label]) => ({
    value,
    label,
  }));

export function labelChangeType(type: string, apiLabel?: string | null): string {
  const trimmed = (apiLabel || '').trim();
  if (trimmed) return trimmed;
  return TYPE_LABELS[type] || type;
}

export function labelChangeStatus(status: string): string {
  return STATUS_LABELS[status] || status;
}

/** Rótulo de transição no combo «Ir para outro status» (ação, não estado). */
export function labelChangeStatusTransition(status: string): string {
  if (status === 'cancelada') return 'Cancelar';
  return labelChangeStatus(status);
}

export function labelGoNoGo(decision?: string | null): string {
  const n = String(decision || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');
  if (n === 'go') return 'Go';
  if (n === 'no_go') return 'No-Go';
  return decision || '—';
}

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  criado: 'Criado',
  status_alterado: 'Status alterado',
  informacao_alterada: 'Informação alterada',
  comentario: 'Comentário',
  anexo: 'Anexo',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  pauta_enviada: 'Pauta enviada',
  distribuido: 'Distribuído',
  atribuido: 'Atribuído',
  assumido: 'Assumido',
  encerrado: 'Encerrado',
};

export function labelActivityType(type: string): string {
  return ACTIVITY_TYPE_LABELS[type] || type;
}

/** Slugs estáveis da trilha change_security_audit_logs → rótulo PT-BR na UI. */
export const SECURITY_AUDIT_EVENT_LABELS: Record<string, string> = {
  deck_salvo: 'Apresentação CAB salva',
  deck_sync_global: 'Slides do modelo aplicados',
  mudanca_criada: 'Mudança criada',
  mudanca_atualizada: 'Mudança editada',
  flag_gestor_mudancas: 'Permissão de gestor de mudanças',
  flag_deck_proprio: 'Permissão de editar próprio CAB',
  flag_auditoria_mudancas: 'Permissão de consultar auditoria',
  formulario_criacao_campo_criado: 'Campo do formulário de criação criado',
  formulario_criacao_campo_atualizado: 'Campo do formulário de criação atualizado',
  formulario_criacao_campo_removido: 'Campo do formulário de criação removido',
  formulario_criacao_campos_reordenados: 'Campos do formulário de criação reordenados',
  email_gatilho_disparado: 'E-mail de gatilho enviado',
  email_gatilho_sem_destinatario: 'E-mail de gatilho sem destinatário',
  email_gatilho_falha_fila: 'Falha no envio agendado (fila)',
  email_gatilho_agendado: 'E-mail de gatilho agendado (fila)',
  email_gatilho_config_criado: 'Configuração de gatilho de e-mail criada',
  email_gatilho_config_atualizado: 'Configuração de gatilho de e-mail alterada',
  email_gatilho_agendamento_cancelado: 'Agendamento de e-mail cancelado',
  subtarefa_criada: 'Subtarefa criada',
  subtarefa_alterada: 'Subtarefa editada',
  subtarefa_assumida: 'Subtarefa assumida',
  subtarefa_concluida: 'Subtarefa concluída',
  subtarefa_conclusao_parcial: 'Subtarefa — conclusão parcial',
  subtarefa_excluida: 'Subtarefa cancelada/excluída',
  chamado_incidente_aberto: 'Chamado Incidente aberto',
  chamado_incidente_agendado: 'Chamado Incidente agendado (gate)',
  chamado_incidente_sincronizado: 'Chamado Incidente sincronizado',
  chamado_incidente_email_abertura: 'E-mail de abertura do chamado (Incidentes)',
  aprovacao_registrada: 'Aprovação registrada (Autorizar/Transporte)',
  reprovacao_registrada: 'Reprovação registrada (Autorizar/Transporte)',
  mudanca_status_alterado: 'Status da mudança alterado',
  mudanca_cancelada: 'Mudança cancelada',
  workflow_status_automatico: 'Status alterado automaticamente (workflow)',
  comentario_incluido: 'Comentário incluído',
  evidencia_incluida: 'Evidência (anexo) incluída',
  evidencia_excluida: 'Evidência (anexo) excluída',
  deliberacao_registrada: 'Deliberação Go/No-Go',
  comite_anotacao_registrada: 'Notas de comitê registradas',
  rollback_executado: 'Rollback executado',
  post_impl_registrado: 'Registro pós-implantação gravado',
  post_impl_form_campo_criado: 'Campo do formulário pós-implantação criado',
  post_impl_form_campo_atualizado: 'Campo do formulário pós-implantação atualizado',
  post_impl_form_campo_removido: 'Campo do formulário pós-implantação removido',
  post_impl_form_campos_reordenados: 'Campos pós-implantação reordenados',
  post_impl_editor_roles_atualizado: 'Quem responde o pós-implantação',
  transporte_aprovacao_redisparda: 'Nova rodada de aprovação de Transporte',
  reuniao_pauta_enviada: 'Convocação de reunião enviada',
  reuniao_pauta_agendada: 'Convocação de reunião agendada (fila)',
  detail_tab_layout_atualizado: 'Ordem das abas da ficha',
  detail_tab_aba_criada: 'Aba da ficha criada',
  detail_tab_abas_reordenadas: 'Abas da ficha reordenadas',
  detail_tab_aba_atualizada: 'Aba da ficha atualizada',
  detail_tab_aba_removida: 'Aba da ficha removida',
  detail_tab_campo_criado: 'Campo de aba da ficha criado',
  detail_tab_campos_reordenados: 'Campos de aba da ficha reordenados',
  detail_tab_campo_atualizado: 'Campo de aba da ficha atualizado',
  detail_tab_campo_removido: 'Campo de aba da ficha removido',
  detail_tab_registro_criado: 'Registro de aba da ficha criado',
  detail_tab_registro_atualizado: 'Registro de aba da ficha atualizado',
  detail_tab_registro_removido: 'Registro de aba da ficha removido',
};

/** Ações de escrita do módulo que ainda não geram linha em /changes/auditoria. */
export const SECURITY_AUDIT_UNAUDITED_LABELS: readonly string[] = [
  'grupos de trabalho e membros',
  'modelos de subtarefa interna',
  'plano de aprovação (slots)',
  'quem edita o plano e status editáveis do formulário',
  'campos extras da aprovação',
  'fluxos de aprovação e políticas por tipo',
  'catálogos administrativos',
  'comitês e integrantes',
  'política emergencial',
  'vínculos Jira',
  'agenda/calendário (SMTP)',
  'cadastro da reunião (só a convocação entra)',
  'formulário da subtarefa externa',
  'respostas de checklist da subtarefa',
  'resumo da ficha',
  'ajustes de transporte SAP',
  'anulação de aprovação (void)',
  'confirmação SMTP do provedor',
];

export function formatSecurityAuditActor(row: {
  actor_label?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
  actor_user_id?: number | null;
}): string {
  const ready = String(row.actor_label || '').trim();
  if (ready) return ready;
  const name = String(row.actor_name || '').trim();
  const email = String(row.actor_email || '').trim();
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  if (row.actor_user_id) return `#${row.actor_user_id}`;
  return 'Sistema';
}

export const SECURITY_AUDIT_EVENT_FILTER_OPTIONS = Object.entries(SECURITY_AUDIT_EVENT_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export function labelSecurityAuditEvent(eventType: string): string {
  const key = String(eventType || '').trim();
  if (!key) return '—';
  if (SECURITY_AUDIT_EVENT_LABELS[key]) return SECURITY_AUDIT_EVENT_LABELS[key];
  const pretty = key.replace(/_/g, ' ').trim();
  if (!pretty) return key;
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

export const FORM_EDITABLE_STATUSES = new Set(['avaliar', 'agendado']);

/** Status que o gestor pode marcar no cadastro para liberar edição do formulário. */
export const FORM_EDITABLE_CONFIGURABLE_STATUSES = Object.keys(STATUS_LABELS).filter(
  (status) => status !== 'encerrar' && status !== 'cancelada',
);
export const SUBTASK_CREATE_STATUSES = new Set(['agendado', 'autorizar']);
export const SUBTASK_OPERATE_STATUSES = new Set([
  'avaliar',
  'agendado',
  'autorizar',
  'transporte',
  'implementar',
]);
export const POST_IMPL_STATUSES = new Set(['revisar', 'encerrar']);

export function isEmergencyChangeType(type?: string | null) {
  return type === 'emergencial';
}
export const FROZEN_STATUSES = new Set(['encerrar', 'cancelada']);

/** Revisar/Encerrar: gestor ou quem vê a mudança (ex.: solicitante) — leitura. */
export function canViewPostImplementation(change: {
  can_view_post_implementation?: boolean;
}) {
  return Boolean(change.can_view_post_implementation);
}

/** Gravação do pós-implantação (Revisar) — conforme cadastro e flag do backend. */
export function canEditPostImplementation(change: {
  can_edit_post_implementation?: boolean;
}) {
  return Boolean(change.can_edit_post_implementation);
}
